import _defineProperty from "/opt/build/repo/website/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/opt/build/repo/website/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "/opt/build/repo/website/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Popper Component
 *
 * The following code is a derivative of the amazing work done by the Material UI team.
 * Original source: https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/Popper/Popper.js
 */
import cx from 'classnames2';
import PopperJS from 'popper.js';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Portal } from '../Portal';
import { createChainedFunction, setRef, useEnhancedEffect, useForkRef } from '../utils';
import styles from './styles.scss';
/**
 * Flips placement if in <body dir="rtl" />
 */

function flipPlacement(placement) {
  // tslint:disable-next-line:no-typeof-undefined
  var direction = typeof window !== 'undefined' && document.body.getAttribute('dir') || 'ltr';

  if (direction !== 'rtl') {
    return placement;
  }

  switch (placement) {
    case 'bottom-end':
      return 'bottom-start';

    case 'bottom-start':
      return 'bottom-end';

    case 'top-end':
      return 'top-start';

    case 'top-start':
      return 'top-end';

    default:
      return placement;
  }
}

function getAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}

var PopperComp = function PopperComp(_ref, ref) {
  var className = _ref.className,
      anchorEl = _ref.anchorEl,
      children = _ref.children,
      container = _ref.container,
      _ref$usePortal = _ref.usePortal,
      usePortal = _ref$usePortal === void 0 ? true : _ref$usePortal,
      _ref$unmountOnExit = _ref.unmountOnExit,
      unmountOnExit = _ref$unmountOnExit === void 0 ? true : _ref$unmountOnExit,
      modifiers = _ref.modifiers,
      isOpen = _ref.isOpen,
      _ref$placement = _ref.placement,
      initialPlacement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
      _ref$popperOptions = _ref.popperOptions,
      popperOptions = _ref$popperOptions === void 0 ? {} : _ref$popperOptions,
      popperRefProp = _ref.popperRef,
      dataTest = _ref.dataTest,
      style = _ref.style,
      rest = _objectWithoutProperties(_ref, ["className", "anchorEl", "children", "container", "usePortal", "unmountOnExit", "modifiers", "isOpen", "placement", "popperOptions", "popperRef", "dataTest", "style"]);

  var tooltipRef = useRef(null);
  var ownRef = useForkRef(tooltipRef, ref);
  var popperRef = useRef(null);
  var handlePopperRef = useForkRef(popperRef, popperRefProp);
  var handlePopperRefRef = useRef(handlePopperRef);
  useEnhancedEffect(function () {
    handlePopperRefRef.current = handlePopperRef;
  }, [handlePopperRef]);
  useImperativeHandle(popperRefProp, function () {
    return popperRef.current;
  }, []);
  var rtlPlacement = flipPlacement(initialPlacement);

  var _useState = useState(rtlPlacement),
      _useState2 = _slicedToArray(_useState, 2),
      placement = _useState2[0],
      setPlacement = _useState2[1];

  if (rtlPlacement !== placement) {
    setPlacement(rtlPlacement);
  }

  var handleOpen = useCallback(function () {
    var popperNode = tooltipRef.current;

    if (!popperNode || !anchorEl || !isOpen) {
      return;
    }

    if (popperRef.current) {
      popperRef.current.destroy();
      handlePopperRefRef.current(null);
    }

    var handlePopperUpdate = function handlePopperUpdate(data) {
      setPlacement(data.placement);
    };

    var popper = new PopperJS(getAnchorEl(anchorEl), popperNode, _objectSpread(_objectSpread({
      placement: rtlPlacement
    }, popperOptions), {}, {
      modifiers: _objectSpread(_objectSpread(_objectSpread({}, usePortal && {
        preventOverflow: {
          boundariesElement: 'window'
        }
      }), modifiers), popperOptions.modifiers),
      onUpdate: createChainedFunction(handlePopperUpdate, popperOptions.onUpdate)
    }));
    handlePopperRefRef.current(popper);
  }, [anchorEl, usePortal, modifiers, isOpen, rtlPlacement, popperOptions]);
  var handleRef = useCallback(function (node) {
    setRef(ownRef, node);
    handleOpen();
  }, [ownRef, handleOpen]);

  var handleClose = function handleClose() {
    if (!popperRef.current) {
      return;
    }

    popperRef.current.destroy();
    handlePopperRefRef.current(null);
  };

  useEffect(function () {
    handleOpen();
  }, [handleOpen]);
  useEffect(function () {
    return function () {
      handleClose();
    };
  }, []);
  useEffect(function () {
    if (!isOpen) {
      handleClose();
    }
  }, [isOpen]);

  if (unmountOnExit && !isOpen) {
    return null;
  }

  var childProps = {
    placement: placement,
    transition: null
  };
  return React.createElement(Portal, {
    isDisabled: !usePortal,
    container: container
  }, React.createElement("div", Object.assign({
    ref: handleRef,
    className: cx(styles.popper, className),
    style: style,
    "data-test": dataTest
  }, rest), typeof children === 'function' ? children(childProps) : children));
}; // Please delete once Voltron and Zeus are on the same version of typescript and use
// export const Popper = forwardRef((props, ref) => ...


var PopperRefWrap = function PopperRefWrap() {
  return forwardRef(PopperComp);
};

export var Popper = PopperRefWrap();